import AlpineClientIframe from 'components/common/alpine-client-iframe';
import useAlpineCartBridgeStatus from 'hooks/use-alpine-cart-bridge-status';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  ALPINE_CART_RESPONSE_TIMEOUT_MESSAGE,
  ALPINE_CART_RESPONSE_TIMEOUT_MS,
  ALPINE_CART_START_SHOPPING_TIMEOUT_MS,
  bridgeUnavailableMessage,
  getAlpineClientUrl,
  isFromAlpineClient,
  readCallbackEvent,
  readReportedBridgeStatus
} from 'utils/alpine-cart-bridge';
import {
  AlpineCartContextType,
  PasteToCartOptions,
  PendingPasteCallbacks
} from 'utils/alpine-cart-bridge-types';

const AlpineCartContext = createContext<AlpineCartContextType | null>(null);

type AlpineCartProviderProps = {
  children: ReactNode;
};

/**
 * Provider component that manages the alpine-client iframe and cart communication.
 * Wrap this around components that need to add items to the cart.
 */
export function AlpineCartProvider({ children }: AlpineCartProviderProps) {
  const alpineClientUrl = getAlpineClientUrl();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    status: bridgeStatus,
    settle,
    markIframeLoaded,
    waitForSettled
  } = useAlpineCartBridgeStatus();

  const pendingCallbackRef = useRef<PendingPasteCallbacks | null>(null);
  const pasteInFlightRef = useRef(false);
  const pasteTimeoutRef = useRef<number>();

  const finishPaste = useCallback(() => {
    window.clearTimeout(pasteTimeoutRef.current);
    pasteTimeoutRef.current = undefined;
    pendingCallbackRef.current = null;
    pasteInFlightRef.current = false;
    setIsLoading(false);
  }, []);

  const failPaste = useCallback(
    (message: string, onError?: (message: string) => void) => {
      finishPaste();
      setError(message);
      onError?.(message);
    },
    [finishPaste]
  );

  useEffect(() => {
    if (!alpineClientUrl) settle('unavailable');
  }, [alpineClientUrl, settle]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isFromAlpineClient(event.origin, alpineClientUrl)) return;

      const reportedStatus = readReportedBridgeStatus(event.data);
      if (reportedStatus) {
        settle(reportedStatus);
        return;
      }

      const callbackEvent = readCallbackEvent(event.data);
      if (!callbackEvent || callbackEvent === 'startShopping') return;

      const pending = pendingCallbackRef.current;
      if (!pending) return;
      finishPaste();

      if (callbackEvent === 'pasteToCart') {
        setError(null);
        pending.onSuccess?.();
        if (pending.redirectUrl) {
          window.location.href = pending.redirectUrl;
        }
        return;
      }

      setError(callbackEvent);
      pending.onError?.(callbackEvent);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [alpineClientUrl, finishPaste, settle]);

  /**
   * Send products to the cart via postMessage.
   * Product IDs should be repeated for quantities > 1.
   */
  const pasteToCart = useCallback(
    async (productIds: string[], options: PasteToCartOptions = {}) => {
      if (pasteInFlightRef.current) return;

      if (productIds.length === 0) {
        failPaste('No products to add to cart', options.onError);
        return;
      }

      pasteInFlightRef.current = true;
      setIsLoading(true);
      setError(null);

      const status = await waitForSettled();
      if (status !== 'ready') {
        failPaste(bridgeUnavailableMessage(status), options.onError);
        return;
      }

      const contentWindow = iframeRef.current?.contentWindow;
      if (!contentWindow) {
        failPaste('Alpine client iframe not ready', options.onError);
        return;
      }

      pendingCallbackRef.current = {
        redirectUrl: options.redirectUrl,
        onSuccess: options.onSuccess,
        onError: options.onError
      };

      contentWindow.postMessage(
        {
          type: 'pasteToCart',
          value: {
            productIds: productIds.join(','),
            clearCart: options.clearCart
          }
        },
        alpineClientUrl
      );

      pasteTimeoutRef.current = window.setTimeout(() => {
        const pending = pendingCallbackRef.current;
        if (!pending) return;
        failPaste(ALPINE_CART_RESPONSE_TIMEOUT_MESSAGE, pending.onError);
      }, ALPINE_CART_RESPONSE_TIMEOUT_MS);
    },
    [alpineClientUrl, failPaste, waitForSettled]
  );

  /**
   * Set the active customer/project context on the alpine-client iframe.
   * Mirrors partnernet's responsiveSiteIntegration startShopping flow.
   */
  const startShopping = useCallback(
    async (
      projectId: string | number,
      customerId: string | number
    ): Promise<boolean> => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_PARTNERNET_URL}/partnernet/responsive/project/startshopping/${projectId}/${customerId}?clearCurrentCart=false`,
          { credentials: 'include' }
        );
        const data = await res.json();
        if (!res.ok) {
          console.error('startShopping API error:', data.errorMessage);
          return false;
        }

        const status = await waitForSettled();
        const contentWindow = iframeRef.current?.contentWindow;
        if (status !== 'ready' || !contentWindow) {
          console.error(
            'Alpine client iframe not ready for startShopping:',
            status
          );
          return false;
        }

        return await new Promise<boolean>((resolve) => {
          const handleCallback = (event: MessageEvent) => {
            if (!isFromAlpineClient(event.origin, alpineClientUrl)) return;
            if (readCallbackEvent(event.data) === 'startShopping') {
              window.removeEventListener('message', handleCallback);
              resolve(true);
            }
          };
          window.addEventListener('message', handleCallback);

          contentWindow.postMessage(
            { type: 'startShopping', value: { ...data } },
            alpineClientUrl
          );

          window.setTimeout(() => {
            window.removeEventListener('message', handleCallback);
            resolve(false);
          }, ALPINE_CART_START_SHOPPING_TIMEOUT_MS);
        });
      } catch (err) {
        console.error('startShopping failed:', err);
        return false;
      }
    },
    [alpineClientUrl, waitForSettled]
  );

  const contextValue = useMemo(
    () => ({
      pasteToCart,
      startShopping,
      bridgeStatus,
      isLoading,
      error
    }),
    [pasteToCart, startShopping, bridgeStatus, isLoading, error]
  );

  return (
    <AlpineCartContext.Provider value={contextValue}>
      <AlpineClientIframe ref={iframeRef} onLoad={markIframeLoaded} />
      {children}
    </AlpineCartContext.Provider>
  );
}

/**
 * Hook to access the alpine cart context.
 * Must be used within an AlpineCartProvider.
 */
export function useAlpineCartContext(): AlpineCartContextType {
  const context = useContext(AlpineCartContext);
  if (!context) {
    throw new Error(
      'useAlpineCartContext must be used within an AlpineCartProvider'
    );
  }
  return context;
}

export function expandProductIds(
  products: Array<{ id: string; qty: number }>
): string[] {
  return products.flatMap((product) => Array(product.qty).fill(product.id));
}
